import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { ResidenceSearchResponse } from 'src/app/interfaces/api';
import { ResidenceService } from 'src/app/services/residence.service';
import { NgIf, NgFor } from '@angular/common';
import { Residence } from '../../models/residence.model';
import { ErrorBlockComponent } from '../error-block/error-block.component';

@Component({
    selector: 'app-residence-lookup',
    templateUrl: './residence-lookup.component.html',
    styleUrls: ['./residence-lookup.component.scss'],
    imports: [
        NgIf,
        NgFor,
        ErrorBlockComponent,
    ],
})
export class ResidenceLookupComponent {
  @ViewChild('lookup') public readonly lookup!: ElementRef;

  @Input() public noResidencesFoundMessage: string | null = '';

  @Output() private readonly lookupComplete = new EventEmitter<number>();

  @Output() private readonly residenceSelected = new EventEmitter<Residence>();

  @Output() private readonly alternateResidence = new EventEmitter<string>();

  @Output() private readonly closeSelf = new EventEmitter<void>();

  public readonly isSearching = signal(false);

  public readonly apiResponse = signal<Residence[] | null>(null);

  @Input()
  public set searchTerm(value: string | null) {
    this.searchTermState.set(value ?? '');
  }

  public readonly searchTermState = signal('');

  private lastValueSearched = '';

  private timeout!: NodeJS.Timeout;

  public readonly lookupError = signal(false);

  constructor(public readonly residenceService: ResidenceService) {}

  public onBlur(event: FocusEvent): void {
    if (event.relatedTarget) {
      this.closeSelf.emit();
    }
  }

  public giveFocus(): void {
    setTimeout(() => {
      const lookupElement = <HTMLInputElement>this.lookup.nativeElement;
      lookupElement.focus();
      lookupElement.select();
      if (this.searchTermState()) {
        this.lookupChange();
      }
    }, 0);
  }

  public lookupChange(): void {
    const lookupElement = <HTMLInputElement>this.lookup.nativeElement;

    clearTimeout(this.timeout);
    this.apiResponse.set(null);
    this.searchTermState.set(lookupElement.value);

    if (this.searchTermState() && this.searchTermState().length > 2) {
      this.timeout = setTimeout(() => {
        this.lookupError.set(false);
        this.isSearching.set(true);
        this.residenceService.searchResidences(this.searchTermState()).subscribe({
          next: (res) => {
            this.isSearching.set(false);
            const residenceResponse = <ResidenceSearchResponse>res;
            if (residenceResponse.data) {
              this.apiResponse.set(residenceResponse.data?.data ?? null);
              this.lookupComplete.emit(this.apiResponse()?.length ?? 0);
            } else {
              this.apiResponse.set(null);
              this.lookupComplete.emit(0);
            }
          },
          error: () => {
            this.lookupError.set(true);
            this.isSearching.set(false);
          },
        });
      }, 250);
    }
  }

  public selectResidence(residence: Residence): void {
    this.searchTermState.set(residence.name);
    this.residenceSelected.emit(residence);
  }

  public doLivingAtHome(): void {
    this.alternateResidence.emit('im-living-at-home');
  }

  public doPrivateLet(): void {
    this.alternateResidence.emit('i-have-a-private-let-or-rental');
  }
}
